import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CarImage } from "@/components/ui/car-image";
import { cn } from "@/lib/utils";
import { getCarImageDisplayCandidates } from "@/lib/car-image-url";

const INTERVAL_MS = 2000;
const FIRST_ADVANCE_MS = 400;

/** Session cache · avoids black flash when returning to listings with browser-cached photos. */
const loadedCardImageUrls = new Set<string>();

function probeCachedImage(url: string): boolean {
  if (loadedCardImageUrls.has(url)) return true;
  const img = new Image();
  img.src = url;
  if (img.complete && img.naturalWidth > 0) {
    loadedCardImageUrls.add(url);
    return true;
  }
  return false;
}

function preloadCardImage(src: string): Promise<void> {
  const candidates = getCarImageDisplayCandidates(src, "card");
  const url = candidates[0]?.src;
  if (!url) return Promise.resolve();

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    const done = () => resolve();
    img.onload = done;
    img.onerror = () => {
      const fallback = candidates[1]?.src;
      if (!fallback) {
        done();
        return;
      }
      const img2 = new Image();
      img2.onload = done;
      img2.onerror = done;
      img2.src = fallback;
    };
    img.src = url;
  });
}

type ListingCardGalleryProps = {
  mainImage: string;
  galleryPreview?: string[];
  alt: string;
  className?: string;
  placeholderClassName?: string;
  /** First slide is LCP candidate · eager + high fetch priority */
  priorityMain?: boolean;
};

/** Stacked gallery · only main image until hover; fast cross-fade without reload spinners. */
export function ListingCardGallery({
  mainImage,
  galleryPreview,
  alt,
  className,
  placeholderClassName = "bg-neutral-100",
  priorityMain,
}: ListingCardGalleryProps) {
  const slides = useMemo(() => {
    const raw =
      galleryPreview && galleryPreview.length > 0 ? galleryPreview : [mainImage];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const url of raw) {
      const trimmed = url?.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      out.push(trimmed);
    }
    return out.length > 0 ? out : [mainImage];
  }, [galleryPreview, mainImage]);

  const hasMultiple = slides.length > 1;
  const firstSlide = slides[0] ?? "";
  const loadedMainRef = useRef<string | null>(null);
  const hoveringRef = useRef(false);
  const [hovering, setHovering] = useState(false);
  const [mountedSlides, setMountedSlides] = useState<Set<number>>(() => new Set([0]));
  const [activeIndex, setActiveIndex] = useState(0);
  const [readySlides, setReadySlides] = useState<Set<number>>(() => new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firstAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMainReady = useCallback(() => {
    loadedCardImageUrls.add(firstSlide);
    loadedMainRef.current = firstSlide;
  }, [firstSlide]);
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (firstAdvanceRef.current) {
      clearTimeout(firstAdvanceRef.current);
      firstAdvanceRef.current = null;
    }
  }, []);

  const markSlideReady = useCallback((index: number) => {
    setReadySlides((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  const onMouseEnter = useCallback(() => {
    if (!hasMultiple || reducedMotion) return;
    hoveringRef.current = true;
    setHovering(true);
    clearTimers();
    setMountedSlides(new Set(slides.map((_, i) => i)));
    markSlideReady(0);

    for (let i = 1; i < slides.length; i++) {
      void preloadCardImage(slides[i]).then(() => {
        if (!hoveringRef.current) return;
        markSlideReady(i);
      });
    }

    void preloadCardImage(slides[1]).then(() => {
      if (!hoveringRef.current) return;
      setActiveIndex(1);
      firstAdvanceRef.current = setTimeout(() => {
        if (!hoveringRef.current) return;
        setActiveIndex((current) => (current + 1) % slides.length);
        intervalRef.current = setInterval(() => {
          if (!hoveringRef.current || document.visibilityState === "hidden") return;
          setActiveIndex((current) => (current + 1) % slides.length);
        }, INTERVAL_MS);
      }, FIRST_ADVANCE_MS);
    });
  }, [clearTimers, hasMultiple, markSlideReady, reducedMotion, slides]);

  const displayIndex = useMemo(() => {
    if (!hovering) return 0;
    if (activeIndex === 0) return 0;
    if (readySlides.has(activeIndex)) return activeIndex;
    for (let i = activeIndex - 1; i >= 0; i--) {
      if (i === 0 || readySlides.has(i)) return i;
    }
    return 0;
  }, [hovering, activeIndex, readySlides]);

  const onMouseLeave = useCallback(() => {
    hoveringRef.current = false;
    setHovering(false);
    clearTimers();
    setActiveIndex(0);
    setMountedSlides(new Set([0]));
    if (loadedMainRef.current === firstSlide) {
      setReadySlides(new Set([0]));
    } else {
      setReadySlides(new Set());
    }
  }, [clearTimers, firstSlide]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useLayoutEffect(() => {
    const url = getCarImageDisplayCandidates(firstSlide, "card")[0]?.src;
    if (url && probeCachedImage(url)) {
      loadedMainRef.current = firstSlide;
      setReadySlides(new Set([0]));
      return;
    }
    if (loadedMainRef.current === firstSlide) {
      return;
    }
    setActiveIndex(0);
    setMountedSlides(new Set([0]));
    setReadySlides(new Set());
    void preloadCardImage(firstSlide);
  }, [firstSlide, slides.length]);

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden bg-neutral-100",
        placeholderClassName,
        className,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {slides.map((src, index) => {
        if (!mountedSlides.has(index)) return null;

        const isVisible = displayIndex === index;

        const isPrioritySlide = Boolean(priorityMain && index === 0);

        return (
          <CarImage
            key={`${index}:${src}`}
            src={src}
            alt={alt}
            preset="card"
            width={1600}
            height={1200}
            priority={isPrioritySlide}
            className={cn(
              "absolute inset-0 h-full w-full bg-neutral-100",
              isVisible && index === 0
                ? "z-[1] opacity-100"
                : isVisible
                  ? "z-[1] opacity-100 transition-opacity duration-300"
                  : "z-0 opacity-0 pointer-events-none transition-opacity duration-300",
            )}
            onLoad={() => {
              if (index === 0) onMainReady();
              else markSlideReady(index);
            }}
            onError={() => {
              if (index === 0) onMainReady();
              else markSlideReady(index);
            }}
          />
        );
      })}
    </div>
  );
}
